import { useEffect, useRef } from 'react';

import { ScrollPriority, useScrollActionManager } from './useScrollActionManager';

export const useIOSKeyboard = () => {
  const isKeyboardVisible = useRef(false);
  const isProgrammaticScroll = useRef(false);
  const isRecentKeypress = useRef(false);
  const lastContentHeight = useRef(0);
  const scrollRetryCount = useRef(0);
  const focusDebounceTimer = useRef<NodeJS.Timeout>();

  const scrollManager = useScrollActionManager();

  const SIGNIFICANT_HEIGHT_REDUCTION_THRESHOLD = 40; // ~40px = 2-3 line breaks worth of content
  const MAX_SCROLL_RETRIES = 3;
  const FOCUS_DEBOUNCE_DELAY = 50;
  const SCROLL_ATTEMPT_DELAY = 50;

  useEffect(() => {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!isIOS || !window.visualViewport) {
      return;
    }

    const handleFocusIn = () => {
      if (focusDebounceTimer.current) {
        clearTimeout(focusDebounceTimer.current);
      }

      focusDebounceTimer.current = setTimeout(() => {
        isKeyboardVisible.current = true;
        scrollRetryCount.current = 0;
        performAutoScroll();
      }, FOCUS_DEBOUNCE_DELAY);
    };

    const performAutoScroll = () => {
      isProgrammaticScroll.current = true;

      scrollManager.addScrollAction(
        () => {
          return new Promise<void>((resolve) => {
            const scrollAttempt = () => {
              // Exit immediately if visualViewport is not available
              if (!window.visualViewport) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                updateEditorHeight();
                finishScroll(resolve);
                return;
              }

              const heightDifference = window.innerHeight - window.visualViewport.height;

              // Retry if keyboard is not detected
              if (heightDifference < 150 && scrollRetryCount.current < MAX_SCROLL_RETRIES) {
                scrollRetryCount.current++;
                setTimeout(scrollAttempt, SCROLL_ATTEMPT_DELAY);
                return;
              }

              // Execute scroll
              window.scrollTo({ top: 0, behavior: 'smooth' });
              updateEditorHeight();
              finishScroll(resolve);
            };

            const updateEditorHeight = () => {
              const editor = document.querySelector('.ProseMirror') as HTMLElement;
              if (editor) {
                lastContentHeight.current = editor.scrollHeight;
              }
            };

            const finishScroll = (resolve: () => void) => {
              setTimeout(() => {
                isProgrammaticScroll.current = false;
                resolve();
              }, 500);
            };

            setTimeout(scrollAttempt, 100);
          });
        },
        ScrollPriority.IOS_KEYBOARD,
        { description: 'iOSKeyboardAutoScroll' },
      );
    };

    const handleFocusOut = () => {
      if (focusDebounceTimer.current) {
        clearTimeout(focusDebounceTimer.current);
      }

      isKeyboardVisible.current = false;
      lastContentHeight.current = 0;
      scrollRetryCount.current = 0;
    };

    const adjustScrollForContentReduction = () => {
      const editor = document.querySelector('.ProseMirror') as HTMLElement;

      // Early return if required elements are not available
      if (!editor || !window.visualViewport) {
        return;
      }

      const currentHeight = editor.scrollHeight;
      const heightReduction = lastContentHeight.current - currentHeight;

      // Early return if height reduction is below threshold
      if (heightReduction <= SIGNIFICANT_HEIGHT_REDUCTION_THRESHOLD) {
        return;
      }

      const editorRect = editor.getBoundingClientRect();
      const viewportHeight = window.visualViewport.height;
      const gapThreshold = viewportHeight * 0.2;

      // Early return if editor position is not appropriate
      if (editorRect.top <= gapThreshold) {
        return;
      }

      performScrollAdjustment(heightReduction, editorRect.top, gapThreshold);
    };

    const performScrollAdjustment = (
      heightReduction: number,
      editorTop: number,
      gapThreshold: number,
    ) => {
      isProgrammaticScroll.current = true;

      const scrollReduction = Math.min(heightReduction, editorTop - gapThreshold);
      window.scrollTo({
        top: window.pageYOffset - scrollReduction,
        behavior: 'smooth',
      });

      setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 500);

      const editor = document.querySelector('.ProseMirror') as HTMLElement;
      if (editor) {
        lastContentHeight.current = editor.scrollHeight;
      }
    };

    const isEditorElement = (target: HTMLElement): boolean => {
      return (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true' ||
        target.closest('[contenteditable="true"]') !== null ||
        target.closest('.ProseMirror') !== null ||
        target.closest('.tiptap') !== null ||
        target.closest('[data-tiptap-editor]') !== null
      );
    };

    const handleContentReduction = () => {
      const editor = document.querySelector('.ProseMirror') as HTMLElement;

      // Early return if editor is not found
      if (!editor) {
        return;
      }

      const initialHeight = editor.scrollHeight;
      const initialScrollTop = window.pageYOffset;

      const observer = new MutationObserver(() => {
        const currentHeight = editor.scrollHeight;
        const heightReduction = initialHeight - currentHeight;

        // Continue processing if no height reduction
        if (heightReduction <= 0) {
          return;
        }

        isProgrammaticScroll.current = true;
        const newScrollTop = Math.max(0, initialScrollTop - heightReduction);

        window.scrollTo({
          top: newScrollTop,
          behavior: 'instant',
        });

        observer.disconnect();

        setTimeout(() => {
          isProgrammaticScroll.current = false;
        }, 200);
      });

      observer.observe(editor, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Stop monitoring after timeout
      setTimeout(() => {
        observer.disconnect();
      }, 1000);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Early return if keyboard is not visible
      if (!isKeyboardVisible.current) {
        return;
      }

      const isRelevantKey =
        event.key === 'Enter' || event.key === 'Backspace' || event.key === 'Delete';

      // Early return if key is not relevant
      if (!isRelevantKey) {
        return;
      }

      const target = event.target as HTMLElement;

      // Early return if not an editor element
      if (!isEditorElement(target)) {
        return;
      }

      isRecentKeypress.current = true;

      if (event.key === 'Backspace' || event.key === 'Delete') {
        handleContentReduction();
      } else {
        setTimeout(() => {
          adjustScrollForContentReduction();
        }, 50);
      }

      setTimeout(() => {
        isRecentKeypress.current = false;
      }, 300);
    };

    const handleClickOutside = (event: MouseEvent) => {
      // Early return if keyboard is not visible
      if (!isKeyboardVisible.current) {
        return;
      }

      const target = event.target as HTMLElement;
      const isInputElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true' ||
        target.closest('[contenteditable="true"]') ||
        target.closest('.ProseMirror') ||
        target.closest('.tiptap') ||
        target.closest('[data-tiptap-editor]');

      // Early return if it's an input element
      if (isInputElement) {
        return;
      }

      // Remove focus
      const proseMirrorElement = document.querySelector('.ProseMirror') as HTMLElement;
      if (proseMirrorElement) {
        proseMirrorElement.blur();
      }

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    const handleScroll = () => {
      // Early return based on conditions to skip scroll processing
      if (!isKeyboardVisible.current || isProgrammaticScroll.current || isRecentKeypress.current) {
        return;
      }

      // Remove focus
      const proseMirrorElement = document.querySelector('.ProseMirror') as HTMLElement;
      if (proseMirrorElement) {
        proseMirrorElement.blur();
      }

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (focusDebounceTimer.current) {
        clearTimeout(focusDebounceTimer.current);
      }

      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollManager]);
};
